import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import { SiteLocale } from '../../graphql/datoSchema.generated'
import stripUndefined from '../../utils/stripUndefined'
import BlockZone, { normalizeTypename } from '../../components/BlockZone'
import blocks from '../../blocks'
import { blocksGetStaticProps } from '../../blocks/index.server'
import notEmpty from '../../utils/notEmpty'
import Layout from '../../components/Layout'
import { getArticle } from '../../graphql/article'
import { ArticleDetailFragment } from '../../graphql/article.generated'
import TextWithImageSection from '../../sections/TextWithImageSection'
import convertDatoImage from '../../utils/convertDatoImage'
import ArticleMetadataSection from '../../sections/ArticleMetadataSection'
import { BaseArticleFragment, getArticleList } from '../../graphql/articleList'
import RelatedArticlesSection from '../../sections/RelatedArticlesSection'
import { getSiteConfig, SiteConfig } from '../../graphql/siteConfig'
import { getLayout, Layout as LayoutType } from '../../graphql/layout'
import { getLocales, getSiteId } from '../../utils/env'
import absolutify from '../../utils/absolutify'
import Seo from '../../components/Seo'
import resolveUrlForRecord from '../../utils/resolveUrlForRecord'
import getAllPages from '../../utils/getAllPages'

const getArticleSlugs = async () => {
  const siteId = getSiteId()

  return (
    (
      await Promise.all(
        getLocales().map(async (locale) => {
          const articleListPages = await getAllPages(
            (page) =>
              getArticleList(siteId, locale, undefined, undefined, 100, page),
            100,
          )
          const articles = articleListPages.map((page) => page.articles).flat()
          return articles.map(({ slug }) => ({
            slug,
            locale,
          }))
        }),
      )
    )
      .flat()
      // Remove pages without valid slug (this should never happen since it's required in the CMS)
      .filter(
        (page): page is typeof page & { slug: string } =>
          page.slug !== undefined && page.slug !== null,
      )
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.FEATUREFLAG_STATICPATHS !== 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  return {
    paths: (await getArticleSlugs()).map(({ slug, locale }) => ({
      params: { slug },
      locale,
    })),
    fallback: 'blocking',
  }
}

export interface StaticProps {
  siteConfig: SiteConfig
  layout: LayoutType
  article: ArticleDetailFragment
  relatedArticles: BaseArticleFragment[]
  // [typename]: { [propName]: unknown }
  blockData: Record<string, Record<string, unknown>>
}

export const getStaticProps: GetStaticProps<
  StaticProps,
  { slug: string | string[] }
> = async ({ params, locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const siteId = getSiteId()
  const slugPath = Array.isArray(params?.slug)
    ? params?.slug?.join('/').toLowerCase()
    : params?.slug?.toLowerCase()
  const slug = slugPath ?? ''
  if (!slug) {
    throw new Error('Missing required param: slug')
  }

  const [siteConfig, layout, article] = await Promise.all([
    getSiteConfig(siteId, locale as SiteLocale),
    getLayout(siteId, locale as SiteLocale),
    getArticle(siteId, locale as SiteLocale, slug),
  ])

  if (!article) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const relatedArticlesCount = 3
  const relatedArticles = (
    await getArticleList(
      siteId,
      locale as SiteLocale,
      article.articleType?.id,
      undefined,
      relatedArticlesCount + 1, // 1 extra to filter out self
    )
  ).articles
    .filter((related) => related.slug !== article.slug)
    .slice(0, relatedArticlesCount)

  const blockData = (
    await Promise.all(
      article.content.map(async (block) => {
        const typename = normalizeTypename(block.__typename)
        const getStaticProps = blocksGetStaticProps[typename]
        if (!getStaticProps) {
          return undefined
        }

        return [
          typename,
          await getStaticProps({
            siteId,
            locale: locale as SiteLocale,
            block,
          }),
        ] as const
      }),
    )
  )
    .filter(notEmpty)
    .reduce(
      (obj, [typename, props]) => ({
        ...obj,
        [typename]: props,
      }),
      {},
    )

  return {
    props: stripUndefined({
      siteConfig,
      layout,
      article,
      relatedArticles,
      blockData,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      path: `/${siteConfig.newsIndex!.slug}/${slug}`,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

const PageResolver = ({
  siteConfig,
  layout,
  article,
  relatedArticles,
  blockData,
}: StaticProps) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const canonicalPath = `/${siteConfig.newsIndex!.slug}/${article.slug}`

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'news',
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          href: `/${siteConfig.newsIndex!.slug}/`,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          title: siteConfig.newsIndex!.seo?.title || 'News',
        },
        {
          key: 'article',
          href: canonicalPath,
          title: article.title || '?',
        },
      ]}
    >
      <Seo
        title={article.title || undefined}
        description={article.brief || undefined}
        canonical={absolutify(canonicalPath, locale as SiteLocale)}
        paths={(article.allSlugs || []).reduce((obj, { locale, value }) => {
          if (locale && value) {
            // eslint-disable-next-line no-param-reassign
            obj[locale] = resolveUrlForRecord(
              siteConfig,
              {
                __typename: 'ArticleRecord',
                slug: value,
              },
              locale,
            )
          }
          return obj
        }, {} as Record<SiteLocale, string>)}
      />

      <TextWithImageSection
        title={article.title}
        titleVariant="pageTitle"
        text={article.brief}
        image={convertDatoImage(article.image)}
        imageSize="landscape"
        variant="header"
      />
      <ArticleMetadataSection article={article} />
      <BlockZone
        components={blocks}
        content={article.content}
        data={blockData}
      />
      <RelatedArticlesSection
        title={siteConfig.relatedArticles || undefined}
        articles={relatedArticles
          .map(({ slug, title, articleType, image, brief }) => {
            if (
              !slug ||
              !title ||
              !articleType?.title ||
              !articleType.color ||
              !image ||
              !brief
            ) {
              return undefined
            }

            return {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              href: `/${siteConfig.newsIndex!.slug}/${slug}`,
              title,
              type: articleType.title,
              typeColor: articleType.color,
              image: convertDatoImage(image),
              brief,
            }
          })
          .filter(notEmpty)}
      />
    </Layout>
  )
}

export default PageResolver
